import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./LaneLabel.module.css";

interface LaneLabelProps {
    id: number;
    zoneName: string;
    laneName: string;
    capacity: number;
    isActive: boolean;
    backgroundColor?: string;
    variant?: 'compact' | 'full';
}

export const calculateTextColor = (backgroundColor: string): string => {
    // Handle hex colors
    if (backgroundColor.startsWith('#')) {
        const r = parseInt(backgroundColor.slice(1, 3), 16);
        const g = parseInt(backgroundColor.slice(3, 5), 16);
        const b = parseInt(backgroundColor.slice(5, 7), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#000000' : '#ffffff';
    }

    // Handle rgb/rgba colors
    if (backgroundColor.startsWith('rgb')) {
        const rgbMatch = backgroundColor.match(/\d+/g);
        if (rgbMatch && rgbMatch.length >= 3) {
            const r = parseInt(rgbMatch[0], 10);
            const g = parseInt(rgbMatch[1], 10);
            const b = parseInt(rgbMatch[2], 10);
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            return luminance > 0.5 ? '#000000' : '#ffffff';
        }
    }

    // Default to black text
    return '#000000';
};

export const LaneLabel: React.FC<LaneLabelProps> = ({
    id,
    zoneName,
    laneName,
    capacity,
    isActive,
    backgroundColor,
    variant = 'compact'
}) => {
    const { t } = useTranslation();

    const bgColor = backgroundColor || '#e5e5e5';
    const textColor = calculateTextColor(bgColor);

    if (variant === 'compact') {
        return (
            <div
                key={id}
                className={styles.laneLabel}
                style={{
                    backgroundColor: bgColor,
                    color: textColor,
                }}
            >
                <span className={styles.laneName}>{`${zoneName} / ${laneName}`}</span>
                <span className={styles.laneCapacity}>{capacity}</span>
                <span
                    className={styles.laneStatus}
                >
                    {isActive ? t("general.open") : t("general.closed")}
                </span>
            </div>
        );
    }

    return (
        <div
            key={id}
            className={styles.laneLabelFull}
            style={{
                backgroundColor: bgColor,
                color: textColor,
            }}
        >
            <div className={styles.laneFullName}>{`${zoneName} / ${laneName}`}</div>
            <div>{t("kmos.labels.capacity")}: {capacity}</div>
            <div className={styles.laneStatusFull}>
                {isActive ? t("general.open") : t("general.closed")}
            </div>
        </div>
    );
};
